import AssetsList from '../assets/AssetsListV2';
import AssetSelectorPanel from '../issues/AssetSelectorPanel';
import { useBoolean } from '@fluentui/react-hooks';
import { updateWork } from '@/services/workService';
import { useMemo, useState } from 'react';
import { useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
  messageBarAtom,
  successMessageAtom,
} from '@/atoms/messageBarAtoms';
import ConfirmDialog from '../../common/ConfirmDialog';
import { MessageBarType, Panel, PanelType } from '@fluentui/react';
import AssetDetailsV2 from '../assets/AssetDetailsV2';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import ImportAsset from '../import/ImportAsset';
import type { IImportedAsset } from '../../services/importService';

type PendingAssetAction = {
  type: 'add' | 'remove';
  assetId: number;
  assetCode?: string;
  assetName?: string;
  assetUuid?: string;
};

export interface IWorkAssetsProps {
  workId?: number;
  assetIds?: number[];
  onAssetIdsChanged?: (assetIds: number[]) => void;
}

const WorkAssets = ({
  workId,
  assetIds,
  onAssetIdsChanged,
}: IWorkAssetsProps) => {
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setMessageBar = useSetAtom(messageBarAtom);

  const [
    isAssetSelectorOpen,
    { setTrue: showAssetSelector, setFalse: hideAssetSelector },
  ] = useBoolean(false);

  const [isConfirmOpen, { setTrue: showConfirm, setFalse: hideConfirm }] =
    useBoolean(false);
  const [
    isCreateAssetPanelOpen,
    { setTrue: showCreateAssetPanel, setFalse: hideCreateAssetPanel },
  ] = useBoolean(false);
  const [
    isImportAssetPanelOpen,
    { setTrue: showImportAssetPanel, setFalse: hideImportAssetPanel },
  ] = useBoolean(false);
  const [pendingAction, setPendingAction] = useState<PendingAssetAction | null>(
    null
  );

  const confirmMessage = useMemo(() => {
    if (!pendingAction) return '';
    if (pendingAction.type === 'remove') {
      return `The asset with id ${pendingAction.assetId} will be removed from the Work.`;
    }

    const label =
      pendingAction.assetCode && pendingAction.assetName
        ? `${pendingAction.assetCode}|${pendingAction.assetName}`
        : pendingAction.assetId;
    return `Asset "${label}" will be added to the Work.`;
  }, [pendingAction]);

  const updateRelatedAssets = async (
    nextAssetIds: number[],
    messages: { persisted: string; draft: string },
    options?: { hideSelector?: boolean }
  ): Promise<boolean> => {
    if (!workId) {
      onAssetIdsChanged?.(nextAssetIds);
      setMessageBar(messages.draft, MessageBarType.info);
      if (options?.hideSelector) {
        hideAssetSelector();
      }
      return true;
    }
    try {
      setIsInProgress(true);
      const abortController = new AbortController();
      await updateWork(abortController, workId, {
        relatedAssetIds: nextAssetIds,
      });
      onAssetIdsChanged?.(nextAssetIds);
      setSuccessMessage(messages.persisted);
      if (options?.hideSelector) {
        hideAssetSelector();
      }
      return true;
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
      return false;
    } finally {
      setIsInProgress(false);
    }
  };

  const removeAsset = async (assetId: number): Promise<void> => {
    const nextAssetIds = (assetIds ?? []).filter((id) => id !== assetId);
    await updateRelatedAssets(nextAssetIds, {
      persisted: 'Asset removed from Work successfully.',
      draft: 'Asset removed from draft. Save Work to persist changes.',
    });
  };

  const handleAssetRemoveClick = (assetId: number): void => {
    setPendingAction({ type: 'remove', assetId });
    showConfirm();
  };

  const addAsset = async (
    assetId: number,
    _assetCode: string,
    _assetName: string,
    _assetUuid: string
  ): Promise<boolean> => {
    const nextAssetIds = (assetIds ?? []).includes(assetId)
      ? assetIds ?? []
      : [...(assetIds ?? []), assetId];
    return await updateRelatedAssets(
      nextAssetIds,
      {
        persisted: 'Asset added to Work successfully.',
        draft: 'Asset added to draft. Save Work to persist changes.',
      },
      { hideSelector: true }
    );
  };

  const handleAssetAddClick = async (
    assetId: number,
    assetCode: string,
    assetName: string,
    assetUuid: string
  ): Promise<void> => {
    setPendingAction({
      type: 'add',
      assetId,
      assetCode,
      assetName,
      assetUuid,
    });
    showConfirm();
  };

  const handleConfirm = async (): Promise<void> => {
    const action = pendingAction;
    hideConfirm();
    setPendingAction(null);
    if (!action) return;

    if (action.type === 'remove') {
      await removeAsset(action.assetId);
      return;
    }

    await addAsset(
      action.assetId,
      action.assetCode ?? '',
      action.assetName ?? '',
      action.assetUuid ?? ''
    );
  };

  const handleAssetsImported = async (
    importedAssets: IImportedAsset[]
  ): Promise<void> => {
    const importedAssetIds = Array.from(
      new Set(
        importedAssets
          .map((asset) => asset.id)
          .filter(
            (assetId): assetId is number =>
              typeof assetId === 'number' && assetId > 0
          )
      )
    );

    const newImportedAssetIds = importedAssetIds.filter(
      (assetId) => !(assetIds ?? []).includes(assetId)
    );

    if (newImportedAssetIds.length === 0) {
      hideImportAssetPanel();
      return;
    }

    onAssetIdsChanged?.([...(assetIds ?? []), ...newImportedAssetIds]);
    setSuccessMessage('Imported assets linked to Work successfully.');
    hideImportAssetPanel();
  };

  return (
    <>
      <AssetsList
        isInWorkContext={true}
        assetIds={assetIds}
        onAddAssetClick={showAssetSelector}
        onRemoveAssetClick={handleAssetRemoveClick}
        onCreateAssetClick={showCreateAssetPanel}
        onImportAssetClick={() => {
          if (!workId) {
            setErrorMessage('Save Work before importing assets.');
            return;
          }
          showImportAssetPanel();
        }}
      />
      <AssetSelectorPanel
        isOpen={isAssetSelectorOpen}
        hideAssetSelector={hideAssetSelector}
        selectorPanelHeaderText="Link existing asset to this work"
        onAssetLinkClick={handleAssetAddClick}
      />
      <Panel
        isOpen={isCreateAssetPanelOpen}
        onDismiss={hideCreateAssetPanel}
        closeButtonAriaLabel="Close"
        type={PanelType.custom}
        customWidth="550px"
        headerText="Create new asset"
      >
        {isCreateAssetPanelOpen ? (
          <FluentProvider theme={webLightTheme}>
            <AssetDetailsV2
              itemIdOverride={0}
              backPath=""
              onCancel={hideCreateAssetPanel}
              onAssetCreated={async (createdAsset) => {
                const linked = await addAsset(
                  createdAsset.id,
                  createdAsset.code,
                  createdAsset.name,
                  createdAsset.uuid
                );
                if (linked) {
                  hideCreateAssetPanel();
                }
              }}
            />
          </FluentProvider>
        ) : null}
      </Panel>
      <Panel
        isOpen={isImportAssetPanelOpen}
        onDismiss={hideImportAssetPanel}
        closeButtonAriaLabel="Close"
        type={PanelType.custom}
        customWidth="550px"
        headerText="Import assets"
      >
        {isImportAssetPanelOpen ? (
          <ImportAsset
            onAssetsImported={handleAssetsImported}
            linkEntity={
              workId ? { entityType: 'Work', entityId: workId } : undefined
            }
          />
        ) : null}
      </Panel>
      <ConfirmDialog
        isModalOpen={isConfirmOpen}
        hideModal={() => {
          hideConfirm();
          setPendingAction(null);
        }}
        message={confirmMessage}
        onYesClick={handleConfirm}
      />
    </>
  );
};

export default WorkAssets;
